import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Standalone Player — opened via the URL in the exported CSV.
 * Strips all app chrome (no header, no sidebar) so the link feels like
 * a lightweight pop-out audio player rather than the full Helix app.
 *
 * URL format:  /player?u=<absolute audio url>&label=<optional title>
 */
const Player = () => {
  const [params] = useSearchParams();
  const audioUrl = params.get('u') || '';
  const label = params.get('label') || 'Sales call recording';
  const callId = params.get('call') || '';

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
    } else {
      a.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setCurrentTime(a.currentTime);
    setProgress((a.currentTime / a.duration) * 100);
  };

  const handleSeek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    a.currentTime = (pct / 100) * a.duration;
    setProgress(pct);
  };

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + delta));
  };

  if (!audioUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#05080f] text-slate-300">
        <div className="text-center">
          <p className="text-lg font-medium">Missing audio URL</p>
          <p className="text-sm text-slate-500 mt-2">This link is malformed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#05080f] via-[#0c1118] to-[#05080f] p-4">
      {/* Subtle grid */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      {/* Glow blobs */}
      <div className="fixed -top-32 -left-32 w-96 h-96 rounded-full bg-[#2eb4ac]/15 blur-3xl pointer-events-none" />
      <div className="fixed -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#5fd6cf]/10 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(46,180,172,0.4)] overflow-hidden">
        {/* Top brand strip */}
        <div className="flex items-center gap-2 px-6 py-4 border-b border-white/5">
          <div className="w-7 h-7 rounded-lg bg-[#2eb4ac] flex items-center justify-center">
            <span className="text-sm font-bold text-white">H</span>
          </div>
          <span className="text-sm font-medium text-white tracking-tight">Helix · Player</span>
          <span className="ml-auto text-[10px] font-mono tracking-wider uppercase text-slate-500">
            {callId ? `id ${callId.slice(0, 8)}` : 'standalone'}
          </span>
        </div>

        <div className="px-6 py-7">
          {/* Title */}
          <p className="text-[11px] tracking-[0.25em] font-mono uppercase text-[#5fd6cf] mb-2">Recording</p>
          <h1 className="text-xl font-semibold text-white leading-snug mb-7 break-words" data-testid="player-label">
            {label}
          </h1>

          {/* Waveform-like decoration */}
          <div className="flex items-end justify-center gap-[3px] h-12 mb-6">
            {Array.from({ length: 48 }).map((_, i) => {
              const baseH = 20 + Math.sin(i * 0.6) * 24 + (i % 5) * 6;
              const passed = (i / 48) * 100 < progress;
              return (
                <span
                  key={i}
                  className={`w-[3px] rounded-full transition-colors ${passed ? 'bg-[#5fd6cf]' : 'bg-white/15'}`}
                  style={{ height: `${baseH}%` }}
                />
              );
            })}
          </div>

          {/* Progress */}
          <div
            className="relative w-full h-1 bg-white/10 rounded-full overflow-hidden cursor-pointer mb-2"
            onClick={handleSeek}
            data-testid="player-progress"
          >
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#2eb4ac] to-[#5fd6cf]"
              style={{ width: `${progress}%`, boxShadow: '0 0 12px rgba(95,214,207,0.6)' }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 mb-6">
            <span data-testid="current-time">{formatTime(currentTime)}</span>
            <span data-testid="total-time">{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-5">
            <button
              onClick={() => skip(-15)}
              className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-colors"
              title="Skip back 15s"
              data-testid="player-skip-back"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <polygon points="11 19 2 12 11 5" />
                <polygon points="22 19 13 12 22 5" />
              </svg>
            </button>

            <button
              onClick={togglePlay}
              className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2eb4ac] to-[#5fd6cf] hover:scale-105 active:scale-95 transition-all flex items-center justify-center shadow-[0_0_25px_-5px_rgba(95,214,207,0.7)]"
              data-testid="player-play-btn"
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6 ml-1">
                  <polygon points="6 4 20 12 6 20" />
                </svg>
              )}
            </button>

            <button
              onClick={() => skip(15)}
              className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-colors"
              title="Skip forward 15s"
              data-testid="player-skip-fwd"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <polygon points="13 19 22 12 13 5" />
                <polygon points="2 19 11 12 2 5" />
              </svg>
            </button>
          </div>

          {/* Hidden native audio */}
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />

          {/* Direct download */}
          <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-between">
            <a
              href={audioUrl}
              download
              className="text-xs font-medium text-slate-400 hover:text-[#5fd6cf] transition-colors"
              data-testid="player-download"
            >
              ↓ Download audio
            </a>
            {callId && (
              <a
                href={`/calls/${callId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-[#5fd6cf] hover:text-white transition-colors"
                data-testid="player-open-detail"
              >
                Open full analysis →
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Player;
